import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import Popover from '@mui/material/Popover';
import { produce } from 'immer';
import _ from 'lodash';
import { EditOfferPageContext } from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { type SelectOption } from '@bbkAdminComponents/select/select';
import CheckBox from '@bbkAdminComponents/switches/checkbox/checkbox';
import {
  type HeroImageV1,
  type ModalContentSetItem,
  type RestModalCard,
  type RestOfferCardPack,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { Icons } from '@bbkAdminUtils/icons';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import getIconForItem from '@bbkFunnel/components/loss/loss-icons';
import { type ModalActionHandler } from '@bbkFunnel/components/modal';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import { TitleTheme } from '../sections';
import ModalSimpleActions from '../sections/simple-actions';

import './modal.scss';

type Props = {
  modalInfo: RestOfferCardPack;
  close: () => void;
  handleAction: ModalActionHandler;
  isPreview: boolean | undefined;
};

export type ActionData = ModalContentSetItem & {
  id: string;
};

const cardTopLevelEditorBadgeColors = ['#FEE736', '#FEBA36', '#36C2FE'];
const cardName = (idx: number) => ['A', 'B', 'C'][idx] || '';

export const getOfferCardBadgeEditorTitle = (index: number) => {
  return (
    <div
      className="tw-mb-4 tw-flex tw-rounded-2xl tw-py-2 tw-pl-6 tw-pr-4 tw-text-base"
      style={{ background: cardTopLevelEditorBadgeColors[index] }}
    >
      <div className="tw-flex-grow tw-text-left">{`Offer card ${cardName(index)}`}</div>
    </div>
  );
};

const OfferVerticalCard: React.FC<{
  vCardIndex: number;
  content: ModalContentSetItem;
  handleAction: ModalActionHandler;
  close: () => void;
  card: RestModalCard;
}> = ({ vCardIndex, content, handleAction, close, card }) => {
  const offerContext = useContext(EditOfferPageContext);
  const hideEditorOptions = !offerContext;
  const primaryCardIndex = offerContext
    ? offerContext.offer.cards.findIndex(
        (c) => c.name === offerContext.offer.primary_card_name
      )
    : -1;

  const refsPrefix = card.id + '-' + vCardIndex;
  const refsCtx = useContext(RefsContext);
  const badgeRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-badge`, badgeRef), [badgeRef]);
  const titleRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const activityRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-activity`, activityRef),
    [activityRef]
  );
  const priceRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-price`, priceRef), [priceRef]);
  const disclaimer1Ref = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-disclaimer1`, disclaimer1Ref),
    [disclaimer1Ref]
  );
  const disclaimer2Ref = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-disclaimer2`, disclaimer2Ref),
    [disclaimer2Ref]
  );
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);

  const cardTopEditorAnchorRef = useRef(null);
  const [openCardTopEditorDropdown, setOpenCardTopEditorDropdown] =
    useState<boolean>(false);

  const [openAddSectionMenu, setOpenAddSectionMenu] =
    useState<React.RefObject<HTMLButtonElement>>();
  const sectionRefs = useRef<Array<React.RefObject<HTMLButtonElement>>>([]);

  const [openReorderSectionMenu, setOpenReorderSectionMenu] =
    useState<React.RefObject<HTMLButtonElement>>();
  const reorderMenuRefs = useRef<Array<React.RefObject<HTMLButtonElement>>>([]);

  const handleOpenAddSectionMenu = (e: any) => {
    setOpenAddSectionMenu(e);
  };

  const handleCloseAddSectionMenu = () => {
    setOpenAddSectionMenu(undefined);
  };

  const handleOpenReorderSectionMenu = (e: any) => {
    setOpenReorderSectionMenu(e);
  };

  const handleCloseReorderSectionMenu = () => {
    setOpenReorderSectionMenu(undefined);
  };

  const theme = useTheme() as EmotionTheme;

  const hero = content.hero;
  const imgUrl = (hero as HeroImageV1).url;
  const description = (
    <CustomerHtml value={_.get(content, ['info', 'description'], '')} />
  );
  const hideDescription = content?.info?.hide_description ?? false;

  const actionData: ActionData = {
    id: card.id + '-' + vCardIndex,
    ...content,
  };

  const hideImage = content?.hero?.hide_image ?? false;

  const title = content?.info?.title ?? '';
  const hideTitle = content?.info?.hide_title ?? false;

  const currentPrice = content?.price?.current_price ?? '';
  const newPrice = content?.price?.new_price ?? '';
  const frequency = content?.price?.frequency ?? '';
  const hideNewPrice = content?.price?.hide_new_price ?? false;
  const hidePrice = content?.price?.hide_price_section ?? false;

  const badgeText = content?.badge?.title ?? '';
  const hideBadge = content?.badge?.hide_badge ?? false;

  const hideCard = content?.hide_card ?? false;
  const activity = content?.activity;
  const hideActivity = content?.activity?.hide_activity ?? false;

  const disclaimer1 = content?.info?.disclaimer1;
  const hideDisclaimer1 = content?.info?.hide_disclaimer1 ?? false;

  const disclaimer2 = content?.info?.disclaimer2;
  const hideDisclaimer2 = content?.info?.hide_disclaimer2 ?? false;

  const hideActionBtn = content?.forwards?.hide_action ?? false;

  const fillColor = _.get(card, 'style_overlay.fillColor', theme.primaryColor);
  const borderRadius = theme.borderRadius ?? '10px';
  const ctaList = _.get(content, ['forwards', 'cta_details_list'], []);
  const planChangeAndDiscount = ctaList.length > 1;

  const renderActivityIcon = ({
    bulletImage,
    bulletIcon,
  }: {
    bulletImage: string | undefined;
    bulletIcon: string | undefined;
  }) => {
    if (bulletImage) {
      return (
        <img
          srcSet={`${bulletImage} 2x`}
          alt=""
          style={{ width: '100%', maxWidth: '22px' }}
        />
      );
    }

    if (bulletIcon) {
      return getIconForItem(bulletIcon, fillColor);
    }

    return undefined;
  };

  enum CardSectionKeys {
    TOP_BADGE = 'top-badge',
    TITLE = 'title',
    IMAGE = 'image',
    DESCRIPTION = 'description',
    ACTIVITY = 'activity',
    PRICE = 'price',
    DISCLAIMER1 = 'disclaimer1',
    DISCLAIMER2 = 'disclaimer2',
    BUTTON_ACTION = 'button-action',
  }

  /*
    vertical offer card sections:
    - top offer badge section
    - card title section
    - card description section
    - card activity section
    - card price section
    - card disclaimer1 section
    - card disclaimer2 section
    - button action section
  */

  const badgeColorIndex = _.get(content, ['badge', 'badge_color']);
  const badgeColor = new TinyColor(
    theme?.offerCardBadgeColors?.[badgeColorIndex]
  );

  const badgeTextColorIndex = _.get(content, ['badge', 'badge_text_color']);
  const badgeTextColor = new TinyColor(
    theme?.offerCardBadgeTextColors?.[badgeTextColorIndex]
  );

  const topOfferBadgeSection = (
    <div
      ref={badgeRef}
      className="tw-py-1 tw-text-center tw-text-white"
      style={{
        background: badgeColor.isValid ? badgeColor.toRgbString() : '#FF800A',
        color: badgeTextColor.isValid ? badgeTextColor.toRgbString() : 'white',
      }}
    >
      <CustomerHtml value={badgeText} />
    </div>
  );

  const titleSection = (
    <div
      className="tw-px-6 tw-py-2 tw-text-center tw-text-4xl tw-font-bold"
      ref={titleRef}
    >
      <TitleTheme title={title} />
    </div>
  );

  const imageSection = (
    <img
      className="tw-px-6 tw-py-4"
      ref={imageRef}
      alt=""
      src={imgUrl}
      css={css`
        width: 100%;
        object-fit: cover;
        object-position: center;
        max-height: 250px;
      `}
    />
  );

  const descriptionSection = (
    <div ref={descRef} className="tw-px-6 tw-py-2">
      <CustomerHtml value={description} />
    </div>
  );

  const activitySection = (
    <div ref={activityRef} className="tw-px-6 tw-py-2">
      {activity?.item_list?.map((activityItem: string) => (
        <div className="tw-mb-2 tw-flex">
          <div className="li-icon">
            {renderActivityIcon({
              bulletIcon: activity.bullet_icon,
              bulletImage: activity.bullet_image,
            })}
          </div>
          <CustomerHtml value={activityItem} />
        </div>
      ))}
    </div>
  );

  const priceSection = (
    <div
      ref={priceRef}
      className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-px-6 tw-py-4"
    >
      <span
        className={cs('tw-pr-2', {
          'tw-text-2xl': planChangeAndDiscount && !hideNewPrice,
          'tw-text-4xl': !(planChangeAndDiscount && !hideNewPrice),
        })}
        style={{
          textDecorationColor: 'red',
          textDecorationLine:
            planChangeAndDiscount && !hideNewPrice ? 'line-through' : 'none',
        }}
      >
        <CustomerHtml value={currentPrice} />
      </span>
      {planChangeAndDiscount && !hideNewPrice && (
        <span
          className="tw-pr-2 tw-text-4xl"
          style={{
            fontSize: '2.25rem',
            lineHeight: '2.5rem',
          }}
        >
          <CustomerHtml value={newPrice} />
        </span>
      )}
      <span className="tw-text-2xl">
        <CustomerHtml value={frequency} />
      </span>
    </div>
  );

  const disclaimer1Section = (
    <div ref={disclaimer1Ref} className="tw-px-6 tw-py-2">
      <div className="tw-bg-neutral-200 tw-py-2 tw-text-center">
        <CustomerHtml value={disclaimer1} />
      </div>
    </div>
  );

  const disclaimer2Section = (
    <div ref={disclaimer2Ref} className="tw-px-6 tw-py-2 tw-text-xs">
      <CustomerHtml value={disclaimer2} />
    </div>
  );

  const buttonActionSection = (
    <div ref={actionsRef} className="tw-px-6 tw-py-2">
      <ModalSimpleActions
        onClick={handleAction}
        fullModalInfo={card}
        actionData={actionData}
        hide={close}
        version2Template
      />
    </div>
  );

  const cardSectionsMap: Record<
    CardSectionKeys,
    { ele: JSX.Element; hide: boolean }
  > = {
    [CardSectionKeys.TOP_BADGE]: { ele: topOfferBadgeSection, hide: hideBadge },
    [CardSectionKeys.TITLE]: { ele: titleSection, hide: hideTitle },
    [CardSectionKeys.IMAGE]: { ele: imageSection, hide: hideImage },
    [CardSectionKeys.DESCRIPTION]: {
      ele: descriptionSection,
      hide: hideDescription,
    },
    [CardSectionKeys.ACTIVITY]: { ele: activitySection, hide: hideActivity },
    [CardSectionKeys.PRICE]: { ele: priceSection, hide: hidePrice },
    [CardSectionKeys.DISCLAIMER1]: {
      ele: disclaimer1Section,
      hide: hideDisclaimer1,
    },
    [CardSectionKeys.DISCLAIMER2]: {
      ele: disclaimer2Section,
      hide: hideDisclaimer2,
    },
    [CardSectionKeys.BUTTON_ACTION]: {
      ele: buttonActionSection,
      hide: hideActionBtn,
    },
  };

  const cardSectionsOrder: Array<CardSectionKeys> = useMemo(() => {
    if (!content) return [];
    const defaultOrder = [
      CardSectionKeys.TOP_BADGE,
      CardSectionKeys.TITLE,
      CardSectionKeys.IMAGE,
      CardSectionKeys.DESCRIPTION,
      CardSectionKeys.ACTIVITY,
      CardSectionKeys.PRICE,
      CardSectionKeys.DISCLAIMER1,
      CardSectionKeys.DISCLAIMER2,
      CardSectionKeys.BUTTON_ACTION,
    ];
    return _.get(
      content,
      ['section_order'],
      defaultOrder
    ) as Array<CardSectionKeys>;
  }, [content]);

  const cardSectionElementHidepath: Record<
    CardSectionKeys,
    Array<string | number>
  > = {
    [CardSectionKeys.TOP_BADGE]: ['badge', 'hide_badge'],
    [CardSectionKeys.TITLE]: [],
    [CardSectionKeys.IMAGE]: ['hero', 'hide_image'],
    [CardSectionKeys.DESCRIPTION]: ['info', 'hide_description'],
    [CardSectionKeys.ACTIVITY]: ['activity', 'hide_activity'],
    [CardSectionKeys.PRICE]: ['price', 'hide_price_section'],
    [CardSectionKeys.DISCLAIMER1]: ['info', 'hide_disclaimer1'],
    [CardSectionKeys.DISCLAIMER2]: ['info', 'hide_disclaimer2'],
    [CardSectionKeys.BUTTON_ACTION]: ['forwards', 'hide_action'],
  };

  const addSectionMenuOptions = useMemo(() => {
    let menuOptions: SelectOption<CardSectionKeys>[] = [];
    cardSectionsOrder.forEach((section, index) => {
      const sectionData = cardSectionsMap[section];
      if (sectionData.hide) {
        menuOptions.push({
          label: section,
          value: section,
        });
      }
    });
    return menuOptions;
  }, [cardSectionsOrder, cardSectionsMap]);

  const dispatchRepaintEditButtonEvent = () => {
    const evt = new Event('repaintEditButtons');
    window.dispatchEvent(evt);
  };

  useEffect(() => {
    dispatchRepaintEditButtonEvent();
  }, []);

  const handleHideOffer = () => {
    if (primaryCardIndex < 0 && !offerContext) return;
    const updatedOffer = produce(offerContext.offer, (draft) => {
      _.set(
        draft,
        [
          'cards',
          primaryCardIndex,
          'requires',
          'content_set',
          vCardIndex,
          'hide_card',
        ],
        !hideCard
      );
    });
    offerContext.setOffer(updatedOffer);
  };

  const cardTopEditor = (
    <>
      <button
        className="tw-rotate-90"
        ref={cardTopEditorAnchorRef}
        onClick={() => setOpenCardTopEditorDropdown(true)}
      >
        {Icons.threeDots}
      </button>
      <Popover
        id={'dropdown'}
        open={openCardTopEditorDropdown}
        anchorEl={cardTopEditorAnchorRef.current}
        onClose={() => setOpenCardTopEditorDropdown(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
      >
        <div className="tw-flex tw-p-2 tw-align-middle">
          <CheckBox checked={hideCard} handleChange={handleHideOffer} />
          <span className="tw-px-1">Hide card</span>
        </div>
      </Popover>
    </>
  );

  const handleAddSection = (section: CardSectionKeys, index: number) => {
    const sectionIndex = cardSectionsOrder.indexOf(section);

    let updatedCardSectionsOrder = [...cardSectionsOrder];

    if (sectionIndex !== -1) {
      updatedCardSectionsOrder.splice(sectionIndex, 1);
    }
    updatedCardSectionsOrder.splice(index, 0, section);

    const path = cardSectionElementHidepath[section];

    if (primaryCardIndex < 0 && !offerContext) return;
    const updatedOffer = produce(offerContext.offer, (draft) => {
      _.set(
        draft,
        [
          'cards',
          primaryCardIndex,
          'requires',
          'content_set',
          vCardIndex,
          'section_order',
        ],
        updatedCardSectionsOrder
      );
      _.set(
        draft,
        [
          'cards',
          primaryCardIndex,
          'requires',
          'content_set',
          vCardIndex,
          ...path,
        ],
        false
      );
    });
    offerContext.setOffer(updatedOffer);
    setOpenAddSectionMenu(undefined);
    dispatchRepaintEditButtonEvent();
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    if (primaryCardIndex < 0 && !offerContext) return;
    const updatedCardSectionsOrder = [...cardSectionsOrder];
    const temp = updatedCardSectionsOrder[index] as CardSectionKeys;
    updatedCardSectionsOrder[index] = updatedCardSectionsOrder[
      index - 1
    ] as CardSectionKeys;
    updatedCardSectionsOrder[index - 1] = temp;
    const updatedOffer = produce(offerContext.offer, (draft) => {
      _.set(
        draft,
        [
          'cards',
          primaryCardIndex,
          'requires',
          'content_set',
          vCardIndex,
          'section_order',
        ],
        updatedCardSectionsOrder
      );
    });
    offerContext.setOffer(updatedOffer);
    setOpenReorderSectionMenu(undefined);
    dispatchRepaintEditButtonEvent();
  };

  const handleMoveDown = (index: number) => {
    if (index == cardSectionsOrder.length - 1) return;
    if (primaryCardIndex < 0 && !offerContext) return;
    const updatedCardSectionsOrder = [...cardSectionsOrder];
    const temp = updatedCardSectionsOrder[index] as CardSectionKeys;
    updatedCardSectionsOrder[index] = updatedCardSectionsOrder[
      index + 1
    ] as CardSectionKeys;
    updatedCardSectionsOrder[index + 1] = temp;
    const updatedOffer = produce(offerContext.offer, (draft) => {
      _.set(
        draft,
        [
          'cards',
          primaryCardIndex,
          'requires',
          'content_set',
          vCardIndex,
          'section_order',
        ],
        updatedCardSectionsOrder
      );
    });
    offerContext.setOffer(updatedOffer);
    setOpenReorderSectionMenu(undefined);
    dispatchRepaintEditButtonEvent();
  };

  const getCardSectionName = (key: CardSectionKeys | string) => {
    switch (key) {
      case CardSectionKeys.TOP_BADGE:
        return 'Badge';
      case CardSectionKeys.TITLE:
        return 'Title';
      case CardSectionKeys.IMAGE:
        return 'Image';
      case CardSectionKeys.DESCRIPTION:
        return 'Description';
      case CardSectionKeys.ACTIVITY:
        return 'Activity';
      case CardSectionKeys.PRICE:
        return 'Price';
      case CardSectionKeys.DISCLAIMER1:
        return 'Disclaimer A';
      case CardSectionKeys.DISCLAIMER2:
        return 'Disclaimer B';
      case CardSectionKeys.BUTTON_ACTION:
        return 'Action';
      default:
        return '';
    }
  };

  return (
    <div>
      {offerContext && (
        <div
          className="tw-mb-4 tw-flex tw-rounded-2xl tw-py-1 tw-pl-6 tw-pr-4 tw-text-base tw-text-neutral-800"
          style={{ background: cardTopLevelEditorBadgeColors[vCardIndex] }}
        >
          <div className="tw-flex-grow">{`Offer card ${cardName(vCardIndex)}`}</div>
          {cardTopEditor}
        </div>
      )}
      <div
        className="tw-my-3 tw-box-border tw-bg-white tw-pb-4 tw-shadow-md"
        style={{
          borderRadius: borderRadius,
          maxWidth: '18rem',
          opacity: hideCard ? '0.3' : '1',
        }}
      >
        {cardSectionsOrder.map((section, sectionIndex) => {
          const sectionData = cardSectionsMap[section];
          if (!sectionRefs.current[sectionIndex]) {
            sectionRefs.current[sectionIndex] =
              React.createRef<HTMLButtonElement>();
          }
          if (!reorderMenuRefs.current[sectionIndex]) {
            reorderMenuRefs.current[sectionIndex] =
              React.createRef<HTMLButtonElement>();
          }
          const sectionRef = sectionRefs.current[sectionIndex];
          const reorderBtnRef = reorderMenuRefs.current[sectionIndex];

          if (sectionData.hide) return null;
          return (
            <div>
              {!hideEditorOptions && (
                <div className="reorder-action-menu">
                  <button
                    ref={reorderBtnRef}
                    className="reorder-action-btn"
                    style={{ top: '10px' }}
                    onClick={() =>
                      handleOpenReorderSectionMenu(
                        reorderMenuRefs.current[sectionIndex]
                      )
                    }
                  >
                    {Icons.threeDots}
                  </button>
                  <Popover
                    open={
                      openReorderSectionMenu ===
                      reorderMenuRefs.current[sectionIndex]
                    }
                    anchorEl={openReorderSectionMenu?.current}
                    onClose={handleCloseReorderSectionMenu}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <div className="reorder-menu tw-flex tw-flex-col tw-py-2">
                      <button
                        disabled={sectionIndex === 0}
                        onClick={() => handleMoveUp(sectionIndex)}
                        className={cs('tw-flex tw-items-center tw-p-2', {
                          'tw-opacity-50': sectionIndex === 0,
                        })}
                      >
                        <div className="tw-flex tw-h-8 tw-w-8 -tw-rotate-90 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-300 tw-p-2">
                          {Icons.arrow}
                        </div>
                        <div className="tw-pl-2">Move up</div>
                      </button>
                      <button
                        disabled={sectionIndex === cardSectionsOrder.length - 1}
                        onClick={() => handleMoveDown(sectionIndex)}
                        className={cs('tw-flex tw-items-center tw-p-2', {
                          'tw-opacity-50':
                            sectionIndex === cardSectionsOrder.length - 1,
                        })}
                      >
                        <div className="tw-flex tw-h-8 tw-w-8 tw-rotate-90 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-300 tw-p-2">
                          {Icons.arrow}
                        </div>
                        <div className="tw-pl-2">Move down</div>
                      </button>
                    </div>
                  </Popover>
                </div>
              )}
              <div
                style={{
                  borderTopLeftRadius: sectionIndex === 0 ? borderRadius : '',
                  borderTopRightRadius: sectionIndex === 0 ? borderRadius : '',
                  overflow: 'hidden',
                }}
              >
                {sectionData.ele}
              </div>
              {!hideEditorOptions && addSectionMenuOptions?.length > 0 && (
                <div
                  className={cs('add-section-wrapper', {
                    'show-add-section':
                      openAddSectionMenu === sectionRefs.current[sectionIndex],
                  })}
                >
                  <div className="add-section">
                    <button
                      key={sectionIndex}
                      ref={sectionRef}
                      onClick={() =>
                        handleOpenAddSectionMenu(
                          sectionRefs.current[sectionIndex]
                        )
                      }
                      className={cs('add-section-btn show-add-section-btn', {
                        'add-section-btn-extra':
                          openAddSectionMenu ===
                          sectionRefs.current[sectionIndex],
                      })}
                    >
                      {Icons.plus}
                    </button>
                    <Popover
                      open={
                        openAddSectionMenu === sectionRefs.current[sectionIndex]
                      }
                      anchorEl={openAddSectionMenu?.current}
                      onClose={handleCloseAddSectionMenu}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <div className="tw-flex tw-flex-col tw-px-4 tw-py-2">
                        {addSectionMenuOptions?.map((option) => {
                          return (
                            <button
                              onClick={() =>
                                handleAddSection(option.value, sectionIndex + 1)
                              }
                              className="tw-p-1 tw-text-left"
                            >
                              {`Add ${getCardSectionName(option.label as CardSectionKeys)}`}
                            </button>
                          );
                        })}
                      </div>
                    </Popover>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OfferCardPack: React.FC<Props> = ({
  modalInfo,
  handleAction,
  close,
  isPreview,
}) => {
  const offerContext = useContext(EditOfferPageContext);
  const contentSet = modalInfo.requires?.content_set as
    ModalContentSetItem[] | undefined;
  return (
    <div
      className="offer-card-pack tw-my-2 tw-flex tw-justify-evenly"
      style={{
        background: 'transparent',
        flexWrap: 'wrap',
      }}
    >
      {contentSet?.map((content, index) => {
        const hideCard = !offerContext && _.get(content, 'hide_card', false);
        return hideCard ? undefined : (
          <OfferVerticalCard
            card={modalInfo}
            vCardIndex={index}
            content={content}
            close={close}
            handleAction={handleAction}
          />
        );
      })}
    </div>
  );
};
